import Logger from "neon-frontend-utils/src/logger";
import Components from "../../../constants/Components";
import SectionInfoFields from "../../Participants/constants/SectionInfoFields";
import { ParticipantStatus } from "../constants/ParticipantStatus";
import { PermissionStatus } from "../constants/PermissionStatus";
import ProgramLabels from "../constants/ProgramLabels";
import * as actionType from "./../constants/ActionTypes";

const initialState = {
    adjustedProgramSetting: null,
    alternativeProjects: [],
    appointmentSettings: {},
    assessmentConfig: {
        participantFinished: null,
        productAddOns: [],
        skipCompetencySelection: null,
    },
    assessmentProgramSettings: [],
    availableProductsForParticipant: [],
    belongingGroups: [],
    dashboardData: [],
    disableAmendCompetencies: null,
    groups: [],
    organisationDataForParticipant: {
        organisation: {},
        project: {},
        reportReceivers: [],
    },
    participants: [],
    projectSettings: null,
    totalParticipants: 0,
};

/**
 * Returns the new state
 * @param {Object} state - state
 * @param {Object} action - action
 * @returns {{}} new state
 */
export default function participantsReducer(state = initialState, action) {
    const newState = Object.assign({}, state);

    switch (action.type) {
        case actionType.GET_PARTICIPANTS: {
            try {
                if (typeof action.response !== "object" || action.response === null) {
                    break;
                }

                newState.totalParticipants = Number.isInteger(action.response["hydra:totalItems"])
                    ? action.response["hydra:totalItems"]
                    : 0;

                const participants = Array.isArray(action.response["hydra:member"])
                    ? action.response["hydra:member"]
                    : [];

                newState.participants = participants.map((participantAccount) => {
                    // get email address used for registration, or if participant was not registered yet, the invitation
                    const participantEmail = participantAccount.email;

                    // get demo participant status
                    const isDemoParticipant = participantAccount.isDemoParticipant;

                    let participantStatus = participantAccount.ahrGenericRoleStatus || null;

                    // when closedOn is received from the backend, set status to CLOSED
                    participantStatus = participantAccount.closedOn ? ParticipantStatus.CLOSED : participantStatus;

                    // if completed, attempt to extract permission status and show that instead
                    if (participantStatus === ParticipantStatus.COMPLETED && participantAccount.reportPermission) {
                        participantStatus =
                            Object.keys(PermissionStatus)
                                .filter((key) => PermissionStatus[key] === participantAccount.reportPermission)
                                .toString()
                                .toLowerCase() || participantStatus;
                    }

                    return {
                        appointmentDate: participantAccount.appointmentDate,
                        cancelledOn: participantAccount.cancelledOn,
                        internal: participantAccount.internal,
                        isDemoParticipant,
                        onlineFinishedOn: participantAccount.onlineFinishedOn || null,
                        participantAccount,
                        participantEmail,
                        participantSessionSlug: participantAccount.uuid,
                        presentAt: participantAccount.presentAt,
                        status: participantStatus,
                        uniqueId: participantAccount.id,
                        uuid: participantAccount.ahrUuid,
                    };
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Exception in Participants reducer GET_PARTICIPANTS",
                });
            }

            break;
        }

        case actionType.GET_PARTICIPANT_DETAILS_SUCCESS: {
            const fields = Object.values(action.fields).reduce(
                (acc, field) => {
                    const fieldName = SectionInfoFields.mappedParticipantFields[field.id] || field.id;

                    acc[fieldName] = field.defaultValue;
                    return acc;
                },
                {
                    uuid: action.uuid,
                },
            );

            newState.participants = newState.participants.map((participant) => {
                if (participant.participantSessionSlug === action.uuid) {
                    return {
                        ...participant,
                        participantAccount: {
                            ...participant.participantAccount,
                            ...fields,
                        },
                        participantEmail: fields.email || participant.participantEmail,
                    };
                }
                return participant;
            });

            break;
        }

        case actionType.CLEAR_PARTICIPANTS: {
            newState.participants = initialState.participants;

            break;
        }

        case actionType.UPDATE_PARTICIPANT_STATUS: {
            newState.participants = newState.participants.map((participant) => {
                if (participant.uuid === action.response.uuid) {
                    participant.status = action.response.status;
                }
                return participant;
            });
            break;
        }

        case actionType.GET_ORGANISATION_DATA_FOR_PARTICIPANT: {
            try {
                newState.organisationDataForParticipant.reportReceivers = [];

                action.response.reportReceivers.forEach((reportReceiver, index) => {
                    const infix = reportReceiver.infix ? ` ${reportReceiver.infix} ` : " ";
                    const reportReceiverName = `${reportReceiver.firstName}${infix}${reportReceiver.lastName}`;

                    newState.organisationDataForParticipant.reportReceivers.push({
                        email: reportReceiver.email,
                        label: reportReceiverName,
                        selected: reportReceiver.selected || false,
                        slug: reportReceiver.uuid,
                        uniqueId: index,
                        value: reportReceiver.uuid,
                    });
                });

                let organisationData = {};
                let projectData = {};

                // extract organisation info
                if (action.response.parentOrganisation) {
                    organisationData = {
                        name: action.response.parentOrganisation.name || "",
                        slug: action.response.parentOrganisation.uuid || "",
                    };
                }

                // extract project info
                if (action.response.project) {
                    projectData = {
                        id: action.response.project.id,
                        name: action.response.project.name || "",
                        slug: action.response.project.uuid || "",
                    };
                }

                // add to newState
                newState.organisationDataForParticipant = {
                    ...newState.organisationDataForParticipant,
                    organisation: organisationData,
                    project: projectData,
                };
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Exception in Participants reducer GET_ORGANISATION_DATA_FOR_PARTICIPANT",
                });
            }

            break;
        }

        case actionType.CLEAR_ORGANISATION_DATA_FOR_PARTICIPANT: {
            newState.organisationDataForParticipant.reportReceivers = [];
            newState.organisationDataForParticipant.organisation = {};
            newState.organisationDataForParticipant.project = {};

            break;
        }

        case actionType.GET_ASSESSMENT_CONFIG: {
            try {
                newState.assessmentConfig = {};

                const tempAddOns = [];

                if (action.response.productAddOns) {
                    action.response.productAddOns.forEach((addOn) => {
                        tempAddOns.push({
                            id: addOn.slug,
                            key: addOn.slug,
                            label: addOn.translationKey,
                            productAddOnHandle: addOn.productAddOnHandle || null,
                            selected: addOn.selected,
                            unavailable: addOn.unavailable,
                        });
                    });
                }

                newState.assessmentConfig.productAddOns = tempAddOns;

                newState.assessmentConfig.participantFinished = action.response.participantFinished;

                if (action.response.product) {
                    newState.assessmentConfig.toggleAbilityTests = action.response.product.toggleAbilityTests !== false;
                    newState.assessmentConfig.skipCompetencySelection =
                        action.response.product.skipCompetencySelection === true;
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Exception in Participants reducer GET_ASSESSMENT_CONFIG",
                });
            }

            break;
        }

        case actionType.CLEAR_ASSESSMENT_CONFIG: {
            newState.assessmentConfig = initialState.assessmentConfig;

            break;
        }

        case actionType.GET_ASSESSMENT_PROGRAM_SETTINGS: {
            try {
                newState.assessmentProgramSettings = [];

                // global program config
                newState.assessmentProgramSettings.push({
                    capTestDomainsMandatory: action.response.capTestDomainsMandatory,
                    captestDomainsSelectable: action.response.captestDomainsSelectable,
                    decision: action.response.decision,
                    decisionOverridden: action.response.decisionOverridden,
                    genericRoleStatus: action.response.genericRoleStatus,
                    hasMboLevels: action.response.hasMboLevels,
                    label: ProgramLabels.GLOBAL_CONFIG,
                    product: action.response.product,
                    productHandle: action.response.productHandle,
                    toggleAbilityTests: action.response.toggleAbilityTests !== false,
                    withActiveAbilityTests:
                        action.response.withActiveAbilityTests !== null ? action.response.withActiveAbilityTests : null,
                });

                // requestedEducationLevel config
                if (
                    action.response.withActiveAbilityTests ||
                    (action.response.educationLevel && action.response.educationLevel.value)
                ) {
                    newState.assessmentProgramSettings.push({
                        label: action.response.educationLevel.handle,
                        options: action.response.educationLevel.form.all.choices,
                        value: action.response.educationLevel.value,
                    });
                }

                if (action.response.hasMboLevels) {
                    newState.assessmentProgramSettings.push({
                        label: action.response.mboLevel.handle,
                        options: action.response.mboLevel.form.all.choices,
                        value: action.response.mboLevel.value,
                    });
                }

                // capTestDomain config
                if (action.response.capTestDomain) {
                    const output = {};

                    Object.keys(action.response.capTestDomain.form.all.choices).forEach((key) => {
                        const value = action.response.capTestDomain.form.all.choices[key];

                        output[value] = action.response.capTestDomain.value.includes(value);
                    });

                    newState.assessmentProgramSettings.push({
                        capTestDomain: output,
                        data: action.response.capTestDomain.data || {},
                        label: action.response.capTestDomain.handle, // mapping data for the default cap test set
                    });
                }

                const questionnaires = [];
                const abilityTests = [];

                if (action.response.questionnaires) {
                    Object.values(action.response.questionnaires).forEach((questionnaire) => {
                        if (typeof questionnaire === "object" && questionnaire.questionnaireGroupId) {
                            questionnaire.active = questionnaire.active === "true";
                            questionnaire.icon = questionnaire.questionnaireHandle;

                            if (questionnaire.isAbilityTest) {
                                abilityTests.push(questionnaire);
                            } else {
                                questionnaires.push(questionnaire);
                            }
                        }
                    });
                }

                newState.assessmentProgramSettings.push(
                    {
                        label: ProgramLabels.QUESTIONNAIRES,
                        questionnaires,
                    },
                    {
                        abilityTests,
                        label: ProgramLabels.ABILITY_TESTS,
                    },
                );
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Exception in Participants reducer GET_ASSESSMENT_PROGRAM_SETTINGS",
                });
            }

            break;
        }

        case actionType.GET_PARTICIPANT_SESSION_VALUES: {
            try {
                newState.appointmentSettings = {};

                if (action.response.participantSessionAppointmentDate) {
                    newState.appointmentSettings.participantSessionAppointmentDate =
                        action.response.participantSessionAppointmentDate;
                }

                if (action.response.consultant) {
                    newState.appointmentSettings.consultant = action.response.consultant;
                }

                if (action.response.appointment) {
                    newState.appointmentSettings.appointment = action.response.appointment;
                }

                if (action.response.project) {
                    newState.appointmentSettings.project = action.response.project;
                }

                if (action.response.product) {
                    newState.appointmentSettings.product = action.response.product;
                }

                if (action.response.id) {
                    newState.appointmentSettings.id = action.response.id;
                }
                if (action.response.liveAssessmentDetail?.preferredDeadline) {
                    newState.appointmentSettings.livePreferredDeadline =
                        action.response.liveAssessmentDetail.preferredDeadline;
                }
                if (action.response.liveAssessmentDetail) {
                    newState.appointmentSettings.liveAssessmentDetail = action.response.liveAssessmentDetail;
                }
                if (action.response.liveAssessmentDetail?.location) {
                    newState.appointmentSettings.liveLocation = action.response.liveAssessmentDetail.location;
                }

                newState.assessmentOutcome = action.response.outcome;
                newState.educationLevel = action.response?.educationLevel;
                newState.assessmentGoal = action.response?.participantSetting?.assessmentGoal;
                newState.careerPosition = action.response?.participantSetting?.careerPosition;

                newState.appointmentSettings.isWithInterpretation = action.response?.isWithInterpretation;
                newState.appointmentSettings.hasAbilityTests = action.response?.hasAbilityTests;
                newState.appointmentSettings.activeCompetencies = action.response?.activeCompetencies;
                newState.appointmentSettings.activeCompetenciesWithAliases =
                    action.response?.activeCompetenciesWithAliases;

                if (action.response.liveAssessmentDetail?.withPreAssessmentCall) {
                    newState.appointmentSettings.withPreAssessmentCall =
                        action.response.liveAssessmentDetail?.withPreAssessmentCall;
                    newState.appointmentSettings.managerName = action.response.liveAssessmentDetail?.managerName;
                    newState.appointmentSettings.managerPhone = action.response.liveAssessmentDetail?.managerPhone;
                }

                if (action.response.accountHasRole.email) {
                    newState.appointmentSettings.invitedEmail = action.response.accountHasRole.email;
                }

                if (action.response.accountHasRole.account.email) {
                    newState.appointmentSettings.registeredEmail = action.response.accountHasRole.account.email;
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Exception in Participants reducer GET_PARTICIPANT_SESSION_VALUES",
                });
            }

            break;
        }

        case actionType.GET_PROJECT_SETTINGS: {
            try {
                newState.projectSettings = action.response;
            } catch (error) {
                Logger.instance.error({
                    component: Components.PARTICIPANTS,
                    error,
                    message: "Exception in Participants reducer GET_PROJECT_SETTINGS",
                });
            }

            break;
        }

        case actionType.CLEAR_PROJECT_SETTINGS: {
            newState.projectSettings = initialState.projectSettings;

            break;
        }

        case actionType.CLEAR_APPOINTMENT_SETTINGS: {
            newState.appointmentSettings = initialState.appointmentSettings;

            break;
        }

        case actionType.CLEAR_ASSESSMENT_PROGRAM_SETTINGS: {
            newState.assessmentProgramSettings = initialState.assessmentProgramSettings;

            break;
        }

        case actionType.GET_DASHBOARD_DATA: {
            newState.dashboardData = action.response;

            break;
        }

        case actionType.CLEAR_DASHBOARD_DATA: {
            newState.dashboardData = initialState.dashboardData;

            break;
        }

        case actionType.GET_ADJUSTED_PROGRAM_SETTING: {
            newState.adjustedProgramSetting = action.response.active;

            break;
        }

        case actionType.CLEAR_ADJUSTED_PROGRAM_SETTING: {
            newState.adjustedProgramSetting = initialState.adjustedProgramSetting;

            break;
        }

        case actionType.GET_BELONGING_GROUPS: {
            newState.belongingGroups = action.response;
            break;
        }

        case actionType.CLEAR_BELONGING_GROUPS: {
            newState.belongingGroups = initialState.belongingGroups;
            break;
        }

        case actionType.GET_GROUPS: {
            newState.groups = action.response;
            break;
        }

        case actionType.GET_ALTERNATIVE_PROJECTS: {
            newState.alternativeProjects = action.response;
            break;
        }

        case actionType.CLEAR_ALTERNATIVE_PROJECTS: {
            newState.alternativeProjects = initialState.alternativeProjects;
            break;
        }

        default:
            break;
    }

    return newState;
}
